import React, { useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import ListAltOutlined from "@mui/icons-material/ListAltOutlined";
import PendingActionsOutlined from "@mui/icons-material/PendingActionsOutlined";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import ReportOutlined from "@mui/icons-material/ReportOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import ArchitectureOutlined from "@mui/icons-material/ArchitectureOutlined";

const COLORS = [
  "#0088FE",
  "#FF8042",
  "#00C49F",
  "#FFBB28",
  "#AA66CC",
  "#FF6666",
  "#FFA07A",
  "#20B2AA",
];

const calculateAverageResolutionTime = (workOrders) => {
  const resolved = workOrders.filter((w) => w.resolved_at && w.created_at);
  if (resolved.length === 0) return 0;

  const totalMs = resolved.reduce((acc, w) => {
    const created = new Date(w.created_at);
    const resolvedDate = new Date(w.resolved_at);
    return acc + (resolvedDate - created);
  }, 0);

  const avgMs = totalMs / resolved.length;
  const avgDays = avgMs / (1000 * 60 * 60 * 24);
  return avgDays.toFixed(1);
};

const numberFmt = (n) => new Intl.NumberFormat().format(n);

const percentLabel = (props) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  if (percent < 0.04) return null;

  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.72;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#2F2F2F"
      fontSize={12}
      fontWeight={600}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
};

const NiceTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const { name, value, payload: p } = payload[0];
  const total = p?.__total || 0;
  const pct = total ? Math.round((value / total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.25,
        borderRadius: "12px",
        border: "1px solid rgba(47,99,173,0.10)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
      }}
    >
      <Typography variant="body2" fontWeight={700}>
        {name}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {numberFmt(value)} • {pct}%
      </Typography>
    </Paper>
  );
};

const StatCard = ({ label, value, Icon, color = "primary" }) => (
  <Paper elevation={0} sx={statCardSx}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "12px",
          bgcolor:
            color === "warning"
              ? "rgba(217,119,6,0.12)"
              : color === "error"
              ? "rgba(220,38,38,0.12)"
              : color === "success"
              ? "rgba(22,163,74,0.12)"
              : "rgba(47,99,173,0.10)",
          color:
            color === "warning"
              ? "#d97706"
              : color === "error"
              ? "#dc2626"
              : color === "success"
              ? "#16a34a"
              : "#2f63ad",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {Icon ? <Icon fontSize="small" /> : null}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: "#6b778c", mb: 0.35 }}>
          {label}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            lineHeight: 1.1,
          }}
        >
          {numberFmt(value)}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const WrappedLegend = ({ data }) => {
  const total = data.reduce((a, d) => a + d.value, 0);

  return (
    <Box
      sx={{
        mt: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))" },
        gap: 1.25,
      }}
    >
      {data.map((d, i) => (
        <Box
          key={`${d.name}-${i}`}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              bgcolor: COLORS[i % COLORS.length],
              flex: "0 0 auto",
            }}
          />
          <Typography
            variant="body2"
            sx={{ flex: "1 1 auto", minWidth: 0, wordBreak: "break-word" }}
          >
            {d.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ flex: "0 0 auto" }}
          >
            {total ? Math.round((d.value / total) * 100) : 0}%
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const AnalyticsChart = ({ title, subtitle, data }) => {
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  const inner = smDown ? 52 : 66;
  const outer = smDown ? 98 : 112;

  const dataWithTotals = data.map((d) => ({ ...d }));
  const total = dataWithTotals.reduce((a, d) => a + d.value, 0);
  const enrichedData = dataWithTotals.map((d) => ({ ...d, __total: total }));

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: "16px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
        overflow: "hidden",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          color: "#223142",
          mb: 0.35,
        }}
      >
        {title}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#6b778c",
          mb: 2,
        }}
      >
        {subtitle}
      </Typography>

      {data.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            borderRadius: "12px",
            border: "1px dashed rgba(47,99,173,0.18)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ position: "relative", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <Pie
                  data={enrichedData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={inner}
                  outerRadius={outer}
                  paddingAngle={5}
                  labelLine={false}
                  label={percentLabel}
                >
                  {enrichedData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<NiceTooltip />} />
              </PieChart>
            </ResponsiveContainer>

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
                pointerEvents: "none",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h6" fontWeight={800}>
                {total}
              </Typography>
            </Box>
          </Box>

          <WrappedLegend data={enrichedData} />
        </>
      )}
    </Paper>
  );
};

const MemberAnalyticsPanel = ({
  workOrders = [],
  violations = [],
  violationTypes = [],
  architecturalRequests = [],
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = (_e, newIndex) => setTabIndex(newIndex);

  const totalOrders = workOrders.length;
  const openOrders = workOrders.filter((w) => !w.resolved_at).length;
  const avgResolveTime = calculateAverageResolutionTime(workOrders);

  const subtypeCounts = workOrders.reduce((acc, w) => {
    const subtype = w.subtype_name || "Unspecified";
    acc[subtype] = (acc[subtype] || 0) + 1;
    return acc;
  }, {});

  const subtypeData = Object.entries(subtypeCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const totalViolations = violations.length;
  const openViolations = violations.filter((v) => v.status !== "Resolved").length;

  const typeIdToTitle = violationTypes.reduce((acc, t) => {
    acc[t.id] = t.title || "Untitled";
    return acc;
  }, {});

  const violationTypeData = Object.entries(
    violations.reduce((acc, v) => {
      const typeName = typeIdToTitle[v.ccr_violation_type_id] || "Unspecified";
      acc[typeName] = (acc[typeName] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  const totalACRs = architecturalRequests.length;
  const acrStatusData = Object.entries(
    architecturalRequests.reduce((acc, req) => {
      const status = req.status || "Unspecified";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value }));

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.35,
          }}
        >
          Member Analytics
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Summary metrics and distribution charts for member activity.
        </Typography>
      </Box>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        allowScrollButtonsMobile
        scrollButtons="auto"
        sx={{
          mb: 3,
          minHeight: "auto",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            minHeight: 40,
            color: "#5f6b7a",
            mr: 1,
            px: { xs: 1.5, md: 2 },
            py: 1,
            whiteSpace: "nowrap",
            minWidth: "fit-content",
          },
          "& .Mui-selected": {
            color: "#2f63ad !important",
            backgroundColor: "rgba(47,99,173,0.08)",
          },
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        <Tab label="Work Orders" />
        <Tab label="Violations" />
        <Tab label="Architectural Requests" />
      </Tabs>

      {tabIndex === 0 && (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total Work Orders"
                value={totalOrders}
                Icon={ListAltOutlined}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Open Work Orders"
                value={openOrders}
                Icon={PendingActionsOutlined}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Avg. Resolution Time (days)"
                value={avgResolveTime}
                Icon={AccessTimeOutlined}
                color="success"
              />
            </Grid>
          </Grid>

          <AnalyticsChart
            title="Work Orders by Subtype"
            subtitle="Top 5 work order subtypes by volume."
            data={subtypeData}
          />
        </>
      )}

      {tabIndex === 1 && (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Total Violations"
                value={totalViolations}
                Icon={ReportOutlined}
                color="error"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard
                label="Open Violations"
                value={openViolations}
                Icon={WarningAmberOutlined}
                color="warning"
              />
            </Grid>
          </Grid>

          <AnalyticsChart
            title="Violations by Type"
            subtitle="Breakdown of violations by violation type."
            data={violationTypeData}
          />
        </>
      )}

      {tabIndex === 2 && (
        <>
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total Requests"
                value={totalACRs}
                Icon={ArchitectureOutlined}
                color="primary"
              />
            </Grid>
          </Grid>

          <AnalyticsChart
            title="Architectural Requests by Status"
            subtitle="Status distribution for architectural requests."
            data={acrStatusData}
          />
        </>
      )}
    </Box>
  );
};

const statCardSx = {
  p: 2.5,
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
  transition: "transform .12s ease, box-shadow .2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
  },
};

export default MemberAnalyticsPanel;
